import React, { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import Header from './Header';
import Footer from './Footer';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import ThankYouHero from './components/ThankYouHero';
import TrainingHighlights from './components/TrainingHighlights';
import TestimonialsSection from './components/TestimonialsSection';

const DATE_FORMATS = [
  'MMMM D YYYY',
  'MMMM DD YYYY',
  'MMMM Do YYYY',
  'MMMM D, YYYY',
  'MMMM DD, YYYY',
  'MMMM Do, YYYY',
];

const formatScheduleEntries = (sessions, locationLabel = '') => {
  if (!Array.isArray(sessions)) {
    return [];
  }

  return sessions
    .filter(Boolean)
    .map((session, index) => {
      const dateMoment = moment(session.date, DATE_FORMATS, true);
      const hasValidDate = dateMoment.isValid();
      const headingDate = hasValidDate
        ? `${dateMoment.format('dddd')}, ${dateMoment.format('MMMM Do YYYY')}`
        : session.date || 'Upcoming Session';

      const times = Array.isArray(session.time) ? session.time.filter(Boolean) : [];
      const uniqueTimes = [...new Set(times)];
      const heading =
        uniqueTimes.length > 0 ? `${headingDate} at ${uniqueTimes.join(' & ')}` : headingDate;

      const fallbackVenue = locationLabel || session.stateName || session.state || '';
      const venueName = session.hotel || fallbackVenue;

      const cityStateParts = [session.city, session.state || session.stateName].filter(Boolean);
      const cityState = cityStateParts.join(', ');
      const zipSuffix = session.zipcode ? `${cityState ? ' ' : ''}${session.zipcode}` : '';
      const addressSegments = [];

      if (session.street) {
        addressSegments.push(session.street);
      }

      if (cityState || zipSuffix) {
        addressSegments.push(`${cityState}${zipSuffix}`);
      }

      const address = addressSegments.filter(Boolean).join(', ');

      const lobby = session.lobyName || session.lobbyName || '';
      const timezone = session.timeOfSet || '';

      return {
        id: session.id || `${session.date || 'session'}-${session.hotel || index}`,
        heading,
        venueName,
        address,
        lobby,
        timezone,
      };
    });
};

const ThankYou = () => {
  const [userData, setUserData] = useState(() => {
    const cookieData = Cookies.get('UserData');

    if (!cookieData) {
      return null;
    }

    try {
      return JSON.parse(cookieData);
    } catch (error) {
      console.error('Failed to parse UserData cookie during initialization', error);
      return null;
    }
  });
  const [classDetails, setClassDetails] = useState(null);
  const [fallbackSchedule, setFallbackSchedule] = useState([]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const cookieData = Cookies.get('UserData');

    if (!cookieData) {
      console.warn('UserData cookie missing');
    } else {
      try {
        const parsedCookie = JSON.parse(cookieData);
        setUserData(parsedCookie);
      } catch (error) {
        console.error('Failed to parse UserData cookie', error);
      }
    }

    const storedClassDetails = window.localStorage.getItem('selectedClassDetails');

    if (!storedClassDetails) {
      console.info('selectedClassDetails not found in localStorage');
      return;
    }

    try {
      const parsedClassDetails = JSON.parse(storedClassDetails);
      setClassDetails(parsedClassDetails);
    } catch (error) {
      console.error('Failed to parse selectedClassDetails', error);
      console.log('Raw selectedClassDetails value:', storedClassDetails);
    }
  }, []);

  const currentTime = useMemo(() => moment().format('X'), []);
  const podscribeProduct = userData?.email ?? '';
  const helmetKey = userData?.email ? `user-${userData.email}` : 'default-head';
  const formattedSchedule = useMemo(
    () => formatScheduleEntries(classDetails?.value, classDetails?.key),
    [classDetails]
  );

  useEffect(() => {
    if (formattedSchedule.length > 0 || fallbackSchedule.length > 0) {
      return;
    }

    let isActive = true;
    const controller = new AbortController();

    const fetchFallbackSchedule = async () => {
      try {
        const response = await fetch('https://lp.taxliensuccess.com/sheetData', {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch schedule: ${response.status}`);
        }

        const data = await response.json();

        if (!data || typeof data !== 'object') {
          return;
        }

        const locationKeys = Object.keys(data);

        if (!locationKeys.length) {
          return;
        }

        const preferredLocation =
          userData?.locationName && data[userData.locationName]
            ? userData.locationName
            : locationKeys[0];

        const sessions = data[preferredLocation];
        const formattedFallback = formatScheduleEntries(sessions, preferredLocation);

        if (isActive) {
          setFallbackSchedule(formattedFallback);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch fallback schedule', error);
      }
    };

    fetchFallbackSchedule();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [formattedSchedule.length, fallbackSchedule.length, userData?.locationName]);

  const adScript = `
    (function(w, d) {
      var id = 'pdt-capture',
        n = 'cript';

      if (!d.getElementById(id)) {
        w.spdt = w.spdt || function() {
          (w.spdt.q = w.spdt.q || []).push(arguments);
        };

        var e = d.createElement(n);
        e.id = id;
        e.async = 1;
        e.src = 'https://pixel.byspotify.com/ping.min.js';

        var s = d.getElementsByTagName(n)[0];
        s.parentNode.insertBefore(e, s);
      }

      w.spdt('conf', { key: 'a9d12a39cf7e41c0b0273e19eef95df8' });
      w.spdt('purchase', {
        order_id: '${currentTime}',
        discount_code: '',
        is_new_customer: '',
        value: '',
        currency: '',
      });
    })(window, document);
  `;

  const podscribeScript = `
    (function (w, d) {
      var id = 'podscribe-capture',
        n = 'script';
      var e = d.createElement(n);
      e.id = id;
      e.async = true;
      e.src = 'https://d34r8q7sht0t9k.cloudfront.net/tag.js';
      var s = d.getElementsByTagName(n)[0];
      s.parentNode.insertBefore(e, s);
      e.addEventListener('load', function() {
        w.podscribe('init', { user_id: '8ce08595-0524-45c9-b1a0-62916831de14', advertiser: 'realsuccess' });
        w.podscribe('lead', {
          product: '${podscribeProduct}',
        });
      });
    })(window, document);
  `;

  return (
    <div className="thank-you-page">
      <Helmet key={helmetKey}>
        {/* <!-- Facebook Pixel Code --> */}
        <script>{`
         !function(f,b,e,v,n,t,s)
         {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
         n.callMethod.apply(n,arguments):n.queue.push(arguments)};
         if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
         n.queue=[];t=b.createElement(e);t.async=!0;
         t.src=v;s=b.getElementsByTagName(e)[0];
         s.parentNode.insertBefore(t,s)}(window, document,'script',
         'https://connect.facebook.net/en_US/fbevents.js');
         fbq('init', '1257597491944293');
         fbq('track', 'Lead');
        `}</script>

        {/* <!-- TIKTOK Pixel Code --> */}
        <script>{`
         !function (w, d, t) {
          w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
          ttq.load('CFUH7KBC77U840VNCFO0');
          ttq.track('CompleteRegistration')
          ttq.page();
        }(window, document, 'ttq');
        `}</script>
        {/* <!-- Google tag (gtag.js) --> */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-630361343"></script>
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-630361343');
        `}</script>
        <script>{`
          gtag('event', 'conversion', {
            'send_to': 'AW-630361343/UBxVCKGI19IBEP-ZyqwC',
            'value': 1.0,
            'currency': 'USD'
          });
        `}</script>

        {/* <!-- Event snippet for Submit lead form conversion page --> */}
        <script>{`
          gtag('event', 'conversion', {'send_to': 'AW-48S6E6PYMQ/UBxVCKGI19IBEP-ZyqwC'});
        `}</script>

        {/* <!-- Event snippet for Submit lead form conversion page --> */}
        {/* // Installation script generated by Ad Analytics */}
        <script dangerouslySetInnerHTML={{ __html: adScript }} />

        {/* <!-- Podscribe pixel --> */}
        <script dangerouslySetInnerHTML={{ __html: podscribeScript }} />
        {/* <!-- /Podscribe pixel --> */}
      </Helmet>

      <div className="confirm-btn-clss">

        <img
          src="https://strategicmedia-23-adswizz.attribution.adswizz.com/fire?pixelId=38d69dd6-665f-4033-9b75-5c0f03d44b1b&type=sitevisit&subtype=Purchase1&aw_0_req.gdpr=true&redirectURL=aHR0cHM6Ly9waXhlbC50YXBhZC5jb20vaWRzeW5jL2V4L3JlY2VpdmU_cGFydG5lcl9pZD0yOTk0JjwjaWYgcmVxdWVzdC5saXN0ZW5lcklkP21hdGNoZXMoJ1swLTlhLWZdezh9LVswLTlhLWZdezR9LVswLTlhLWZdezR9LVswLTlhLWZdezR9LVswLTlhLWZdezEyfScpPnBhcnRuZXJfdHlwZWRfZGlkPSU3QiUyMkhBUkRXQVJFX0FORFJPSURfQURfSUQlMjIlM0ElMjIke3JlcXVlc3QubGlzdGVuZXJJZH0lMjIlN0Q8I2Vsc2VpZiByZXF1ZXN0Lmxpc3RlbmVySWQ_bWF0Y2hlcygnWzAtOUEtRl17OH0tWzAtOUEtRl17NH0tWzAtOUEtRl17NH0tWzAtOUEtRl17NH0tWzAtOUEtRl17MTJ9Jyk-cGFydG5lcl90eXBlZF9kaWQ9JTdCJTIySEFSRFdBUkVfSURGQSUyMiUzQSUyMiR7cmVxdWVzdC5saXN0ZW5lcklkfSUyMiU3RDwjZWxzZT5wYXJ0bmVyX2RldmljZV9pZD0ke3JlcXVlc3QubGlzdGVuZXJJZCF9PC8jaWY-"
          alt="Tracking pixel"
        />

      </div>

      <Header />

      <ThankYouHero schedule={formattedSchedule.length ? formattedSchedule : fallbackSchedule} />

      <TrainingHighlights />

      <TestimonialsSection />

      <Footer />

    </div>
  );
};

export default ThankYou;
